import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState, useEffect, JSX } from "react";
import { useTranslation } from "react-i18next";
import { ThreeDotsLoader } from "../loaders";
import { useIsTouchDevice } from "@/hooks/useIsTouchDevice";

/**
 * Represents an option in the combobox dropdown
 * @interface ComboboxOption
 * @property {string|React.ReactNode} label - The display text or node for the option
 * @property {string|number} value - The value of the option
 * @property {boolean} [disabled] - Whether the option is disabled
 * @property {string} [searchValue] - Optional text to use for searching when label is not a string
 */
export type ComboboxOption = {
  label: string | React.ReactNode;
  value: string | number;
  disabled?: boolean;
  searchValue?: string;
};

/**
 * Props for the CustomCombobox component
 * @interface CustomComboboxProps
 * @property {ComboboxOption[]} options - Array of options to display in the dropdown
 * @property {string|number|(string|number)[]|null} [value] - The currently selected value(s)
 * @property {Function} onChange - Callback when selection changes
 * @property {string} [placeholder] - Placeholder text when no option is selected
 * @property {string} [searchPlaceholder] - Placeholder text for the search input
 * @property {string} [emptyMessage] - Message to display when no options match the search
 * @property {boolean} [disabled=false] - Whether the combobox is disabled
 * @property {boolean} [loading=false] - Whether the options are loading
 * @property {boolean} [error=false] - Whether the combobox has an error
 * @property {boolean} [multiple=false] - Whether multiple options can be selected
 * @property {string} [triggerClassName] - Additional class names for the trigger button
 * @property {string} [contentClassName] - Additional class names for the dropdown content
 * @property {Function} [formatSelectedLabel] - Custom formatter for the selected label when multiple=true
 * @property {Object} [popoverProps] - Props to pass to the PopoverContent component
 * @property {boolean} [disableAutoFocus=false] - Whether to disable auto-focus on the search input when opening
 */
interface CustomComboboxProps {
  options: ComboboxOption[];
  value?: string | number | (string | number)[] | null;
  onChange: (_value: string | number | (string | number)[] | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  multiple?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  formatSelectedLabel?: (_selected: (string | number)[]) => string;
  popoverProps?: React.ComponentProps<typeof PopoverContent>;
  disableAutoFocus?: boolean;
};

/**
 * A customizable combobox component with search functionality.
 *
 * Use this component when not using react hook form and need a more customizable combobox, else use ComboboxInputField.
 *
 * Note: If using this component inside of a dialog, you need to pass the following prop to fix nested dialog scrolling:
 * ```tsx
 * popoverProps={{
 *   onWheel: (e) => {
 *     e.stopPropagation();
 *   }
 * }}
 * ```
 *
 * @param {CustomComboboxProps} props - The component props
 * @returns {JSX.Element} The CustomCombobox component
 */
export const CustomCombobox = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  loading = false,
  error = false,
  multiple = false,
  triggerClassName,
  contentClassName,
  formatSelectedLabel,
  popoverProps,
  disableAutoFocus = false,
}: CustomComboboxProps): JSX.Element => {
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isTouchDevice = useIsTouchDevice();

  /**
   * Converts the value prop to an array for consistent handling
   * - For multiple selection: returns array of values or empty array
   * - For single selection: returns array with single value or empty array
   */
  const selectedValues = multiple
    ? (Array.isArray(value) ? value : value ? [value] : [])
    : (value ? [value] : []);

  /**
   * Filters options based on the search query
   * Uses either the label (if string) or a dedicated searchValue property
   */
  const filteredOptions = options.filter(option => {
    const searchableText = typeof option.label === "string"
      ? option.label.toLowerCase()
      : option.searchValue?.toLowerCase() || "";
    return searchableText.includes(searchQuery.toLowerCase());
  });

  /**
   * Handles selection/deselection of an option
   * - For multiple selection: toggles the selected value in the array
   * - For single selection: selects the value or sets to null if already selected
   *
   * @param {string|number} currentValue - The value being selected/deselected
   */
  const handleSelect = (currentValue: string | number) => {
    if (multiple) {
      const newValue = selectedValues.includes(currentValue)
        ? selectedValues.filter(value => value !== currentValue)
        : [...selectedValues, currentValue];
      onChange(newValue.length > 0 ? newValue as (string | number)[] : null);
    } else {
      onChange(selectedValues[0] === currentValue ? null : currentValue);
      setOpen(false);
    }
  };

  /**
   * Formats the display label shown in the combobox trigger button
   * - Shows placeholder if nothing selected
   * - For multiple: shows count or custom formatted label
   * - For single: shows the selected option's label
   *
   * @returns {string|React.ReactNode} Formatted display label
   */
  const getDisplayLabel = () => {
    if (selectedValues.length === 0) return placeholder ?? t("forms.select");

    if (multiple) {
      if (formatSelectedLabel) {
        return formatSelectedLabel(selectedValues as (string | number)[]);
      }
      return `${selectedValues.length} ${t("common:forms.selected")}`;
    }

    const option = options.find(opt => opt.value === selectedValues[0]);
    return option ? option.label : placeholder;
  };

  // Reset search query when dropdown is opened
  useEffect(() => {
    if (open) {
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between",
            error && "border-red-500",
            selectedValues.length === 0 && "text-muted-foreground",
            triggerClassName
          )}
        >
          <span className="truncate">{getDisplayLabel()}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("p-0 w-[--radix-popover-trigger-width]", contentClassName)}
        onOpenAutoFocus={(e) => {
          if (disableAutoFocus || isTouchDevice) {
            e.preventDefault();
          }
        }}
        {...popoverProps}
      >
        <Command shouldFilter={false}> {/* Disable built-in filtering */}
          <CommandInput
            placeholder={searchPlaceholder || t("search")}
            className="h-9"
            onValueChange={setSearchQuery}
            value={searchQuery}
          />
          {
            loading ? (
              <div className="w-full flex justify-center p-2">
                <ThreeDotsLoader
                  visible={true}
                  color="#94A3B8"
                  radius={20}
                  height={6}
                  width={6}
                  ariaLabel="three-dots-loading"
                />
              </div>
            ) : (
              <CommandList>
                <CommandEmpty>{emptyMessage || t("forms.noresults")}</CommandEmpty>
                <CommandGroup>
                  {
                    filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        onSelect={() => handleSelect(option.value)}
                        disabled={option.disabled}
                      >
                        <div className="mr-2 flex h-4 w-4 items-center justify-center">
                          <Check
                            className={cn(
                              "h-4 w-4",
                              selectedValues.includes(option.value)
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </div>
                        {option.label}
                      </CommandItem>
                    ))
                  }
                </CommandGroup>
              </CommandList>
            )
          }
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CustomCombobox;
